import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';

const { FiSearch, FiGrid, FiList, FiPackage, FiLock } = FiIcons;

const Portal = () => {
  const [tools, setTools] = useState([]);
  const [filteredTools, setFilteredTools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserTools();
  }, [user]);

  useEffect(() => {
    filterTools();
  }, [searchTerm, selectedCategory, tools]);

  const fetchUserTools = async () => {
    try {
      setLoading(true);
      // TODO: Echte Datenbank-Abfrage implementieren
      // Platzhalter-Daten für Demo
      const mockTools = [
        {
          id: 1,
          name: 'Tool 1 - Beispiel-Tool',
          description: 'Eine kurze Beschreibung des Tools für den Nutzer.',
          category: 'Analyse',
          slug: 'tool-1',
          icon: 'FiPackage',
          isUnlocked: true
        },
        {
          id: 2,
          name: 'Tool 2 - Premium-Tool',
          description: 'Ein weiteres nützliches Tool für Ihre Arbeit.',
          category: 'Produktivität',
          slug: 'tool-2',
          icon: 'FiPackage',
          isUnlocked: true
        },
        {
          id: 3,
          name: 'Tool 3 - Gesperrt',
          description: 'Dieses Tool ist noch nicht freigeschaltet.',
          category: 'Analyse',
          slug: 'tool-3',
          icon: 'FiLock',
          isUnlocked: false
        }
      ];
      setTools(mockTools);
    } catch (error) {
      console.error('Fehler beim Laden der Tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTools = () => {
    let filtered = tools;

    if (searchTerm) {
      filtered = filtered.filter(tool =>
        tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(tool => tool.category === selectedCategory);
    }

    setFilteredTools(filtered);
  };

  const categories = ['all', ...new Set(tools.map(tool => tool.category))];

  const handleToolClick = (tool) => {
    if (tool.isUnlocked) {
      navigate(`/tool/${tool.slug}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-light)]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[var(--color-primary)] mx-auto mb-4"></div>
            <p className="text-[var(--color-text-light)]">Lade Ihre Tools...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-light)]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-[var(--color-text-dark)] mb-2">
            Ihre Tools
          </h1>
          <p className="text-[var(--color-text-light)] mb-8">
            Willkommen zurück! Hier finden Sie alle Ihre freigeschalteten Tools.
          </p>

          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
              <div className="relative flex-1">
                <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--color-text-light)]" />
                <input
                  type="text"
                  placeholder="Tools durchsuchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-12"
                />
              </div>

              <div className="flex items-center space-x-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="input-field"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'Alle Kategorien' : category}
                    </option>
                  ))}
                </select>

                <div className="flex space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-[var(--color-primary)] text-white'
                        : 'bg-gray-100 text-[var(--color-text-light)] hover:bg-gray-200'
                    }`}
                  >
                    <SafeIcon icon={FiGrid} className="text-xl" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === 'list'
                        ? 'bg-[var(--color-primary)] text-white'
                        : 'bg-gray-100 text-[var(--color-text-light)] hover:bg-gray-200'
                    }`}
                  >
                    <SafeIcon icon={FiList} className="text-xl" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {filteredTools.length === 0 ? (
            <div className="text-center py-12">
              <SafeIcon icon={FiPackage} className="text-6xl text-[var(--color-text-light)] mx-auto mb-4" />
              <p className="text-[var(--color-text-light)] text-lg">
                {searchTerm || selectedCategory !== 'all'
                  ? 'Keine Tools gefunden. Versuchen Sie eine andere Suche.'
                  : 'Sie haben noch keine freigeschalteten Tools.'}
              </p>
            </div>
          ) : (
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                : 'grid-cols-1'
            }`}>
              {filteredTools.map((tool, index) => (
                <motion.div
                  key={tool.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`neon-border-container ${
                    tool.isUnlocked 
                      ? 'cursor-pointer' 
                      : 'opacity-60 cursor-not-allowed'
                  }`}
                  onClick={() => handleToolClick(tool)}
                >
                  <div className="card">
                    <div className="flex items-start space-x-4">
                      <div className={`p-4 rounded-lg ${
                        tool.isUnlocked 
                          ? 'bg-[var(--color-light)]' 
                          : 'bg-gray-100'
                      }`}>
                        <SafeIcon 
                          icon={tool.isUnlocked ? FiPackage : FiLock} 
                          className={`text-3xl ${
                            tool.isUnlocked 
                              ? 'text-[var(--color-primary)]' 
                              : 'text-gray-400'
                          }`} 
                        />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-1">
                          {tool.name}
                        </h3>
                        <p className="text-sm text-[var(--color-text-light)] mb-3">
                          {tool.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium px-3 py-1 bg-[var(--color-light)] text-[var(--color-secondary)] rounded-full">
                            {tool.category}
                          </span>
                          {tool.isUnlocked && (
                            <button className="btn-primary text-sm py-2 px-4">
                              Öffnen
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Portal;